import { MutableRefObject, useCallback, useEffect } from 'react';
import { CalendarBot } from '../services/calendarWebSocketService';
import { recallWebSocketService, RecallSessionEnded } from '../services/recallWebSocketService';
import { disconnectLiveCallSockets } from '../services/liveCallSockets';
import { ConnectedBotState, OutsideEndReport } from './useEndOfCallFlow';
import {
  BOT_NOT_ADMITTED_EVENT,
  BotNotAdmittedEventDetail,
  BotNotAdmittedReason,
} from '../constants/botAdmission';
import { notAdmittedReasonOf } from '../utils/botNotAdmitted';

interface UseBotEndedOutsideOptions {
  /** useEndOfCallFlow's outside-end entry point (runs the flow once per session id). */
  endFromOutside: (bot: OutsideEndReport, live: ConnectedBotState) => boolean;
  /** useEndOfCallFlow's: never run the flow for this session id (a bot never let in). */
  skipEndOfCall: (sessionId: string) => void;
  /** The page's bot state when an end arrives (a ref: calendar callbacks are registered once). */
  connectedBotRef: MutableRefObject<ConnectedBotState>;
  /** The calendar bot list, for the event title session_ended does not carry. */
  calendarBotsRef: MutableRefObject<CalendarBot[]>;
  /** The end-of-call flow started for the page's bot (stable callback). */
  onEndOfCallStarted: () => void;
  /** session_ended for the page's bot: clear the page's bot state (stable callback). */
  onConnectedBotGone: () => void;
  /** The page's bot ended without ever being let in: show why (stable callback). */
  onBotNotAdmitted: (reason: BotNotAdmittedReason) => void;
}

/**
 * The bot ended outside the app: calendar bot_terminated / bot_failed (the
 * returned handler, called by the page's calendar callbacks) or the recall
 * socket's session_ended (subscribed here). Either way the end-of-call flow
 * runs once per session (useEndOfCallFlow de-dupes by session id, so both
 * events arriving show it once) and every live socket closes, so none of them
 * keeps reconnecting to the ended room.
 *
 * A bot_terminated or session_ended saying the bot was never let in
 * (`notAdmitted`, owner decision 2026-10-03) is not a call: no review or
 * summary, now or from a later end report, whichever of the two arrives first.
 * The lobby hears about it (BOT_NOT_ADMITTED_EVENT) and, when it is the page's
 * bot (joining, or out of order while the page thinks it is live, owner or
 * watcher alike), every live socket closes and the page shows why.
 */
export function useBotEndedOutside({
  endFromOutside,
  skipEndOfCall,
  connectedBotRef,
  calendarBotsRef,
  onEndOfCallStarted,
  onConnectedBotGone,
  onBotNotAdmitted,
}: UseBotEndedOutsideOptions): (bot: CalendarBot) => void {
  const handleBotNotAdmitted = useCallback(
    (botId: string, reason: BotNotAdmittedReason) => {
      skipEndOfCall(botId);
      const detail: BotNotAdmittedEventDetail = { botId, reason };
      window.dispatchEvent(new CustomEvent(BOT_NOT_ADMITTED_EVENT, { detail }));
      if (!connectedBotRef.current.connectedBotIds.includes(botId)) return;
      disconnectLiveCallSockets();
      onBotNotAdmitted(reason);
    },
    [skipEndOfCall, connectedBotRef, onBotNotAdmitted]
  );

  const handleBotEndedOutside = useCallback(
    (bot: CalendarBot) => {
      const notAdmittedReason = notAdmittedReasonOf(bot);
      if (notAdmittedReason && bot?.botId) {
        handleBotNotAdmitted(bot.botId, notAdmittedReason);
        return;
      }
      if (!endFromOutside(bot, connectedBotRef.current)) return;
      onEndOfCallStarted();
      disconnectLiveCallSockets();
    },
    [endFromOutside, connectedBotRef, onEndOfCallStarted, handleBotNotAdmitted]
  );

  const handleRecallSessionEnded = useCallback(
    ({ roomId, notAdmitted, notAdmittedReason }: RecallSessionEnded) => {
      // session_ended carries the same outcome as bot_terminated: whichever
      // arrives first shows "Bot not let in", and the other is a no-op.
      const notAdmittedOutcome = notAdmittedReasonOf({ status: 'terminated', notAdmitted, notAdmittedReason });
      if (notAdmittedOutcome) {
        handleBotNotAdmitted(roomId, notAdmittedOutcome);
        // The room is gone whether or not the page still holds the bot.
        disconnectLiveCallSockets();
        return;
      }
      const knownBot = calendarBotsRef.current?.find((bot) => bot?.botId === roomId);
      const report: OutsideEndReport = { botId: roomId, status: 'terminated', eventTitle: knownBot?.eventTitle };
      if (endFromOutside(report, connectedBotRef.current)) onEndOfCallStarted();
      // The room is gone whether or not the page counted itself connected
      // (e.g. a manual bot that never joined): close the sockets regardless.
      disconnectLiveCallSockets();
      if (connectedBotRef.current.connectedBotIds.includes(roomId)) onConnectedBotGone();
    },
    [endFromOutside, connectedBotRef, calendarBotsRef, onEndOfCallStarted, onConnectedBotGone, handleBotNotAdmitted]
  );

  useEffect(() => recallWebSocketService.onSessionEnded(handleRecallSessionEnded), [handleRecallSessionEnded]);

  return handleBotEndedOutside;
}
